"use client";

import classNames from "classnames";
import { FC, SyntheticEvent, useContext } from "react";
import { IChatResponse } from "@finbackoffice/clientbff-client";
import { usePathname, useRouter } from "next/navigation";
import { ChatContext, ClientBFFContext, useSearchParamsQuery } from "@finbackoffice/site-core";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import Translate from "components/base/translate/Translate";
import { useDate, DEFAULT_DATE_FORMAT } from "hooks";
import { Svg } from "components/base/svg/Svg";
import styles from "./conversation.module.sass";

interface IProps {
    checked: boolean;
    triggerMessageChecked: (id: number) => void;
    hasNew: boolean;
    onStarrClickCallback: (id: number, val: boolean) => void;
    conversation: IChatResponse;
    isPageMode: boolean;
}

const Conversation: FC<IProps> = ({
    checked,
    triggerMessageChecked,
    hasNew,
    onStarrClickCallback,
    conversation,
    isPageMode,
}) => {
    const client = useContext(ClientBFFContext);
    const router = useRouter();
    const pathname = usePathname();
    const { getQueryString } = useSearchParamsQuery();
    const { unreadMessages, removeMessage, chatStats, setChatStats } = useContext(ChatContext);
    const { formatDate } = useDate();

    const onStarrClick = async (e: SyntheticEvent) => {
        e.stopPropagation();

        try {
            const response = await client.updateChat(conversation.id, {
                is_starred: !conversation.is_starred,
            });

            if (response) {
                onStarrClickCallback(conversation.id, response.is_starred);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const onConversationClick = () => {
        if (hasNew) {
            const message = unreadMessages.find((message) => conversation.id === message.chat.id);
            if (message) {
                removeMessage(conversation.id, [message.id]);
            } else if (chatStats) {
                setChatStats({
                    unread_chats: chatStats.unread_chats,
                    unread_message: chatStats.unread_message - conversation.unread_message_count,
                });
            }
        }

        if (isPageMode) {
            router.push(`${pathname}/${conversation.id}`);
        } else {
            router.push(`${pathname}?${getQueryString()}&id=${conversation.id}`);
        }
    };

    return (
        <div
            className={classNames(styles.message, hasNew && styles.new)}
            onClick={onConversationClick}>
            <label
                htmlFor={`topic_${conversation.id}`}
                className={classNames("ui-checkbox", styles.checkbox, checked && "checked")}>
                <input type="checkbox" id={`topic_${conversation.id}`} />
                <i
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        return triggerMessageChecked(conversation.id);
                    }}
                />
            </label>
            <div>
                <div className={styles.fromDiv}>
                    <div className={styles.username}>
                        <Translate
                            tid="account_messages_from"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :
                        <span>
                            {conversation.author === "User" ? (
                                <Translate
                                    tid="account_messages_you"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            ) : (
                                conversation.author
                            )}
                        </span>
                    </div>
                    <span className={styles.date}>
                        {formatDate(
                            new Date(conversation.updated_at),
                            `${DEFAULT_DATE_FORMAT} - hh:mm b`,
                            false,
                        )}
                    </span>
                </div>
                <div className={styles.subjectDiv}>
                    <Translate
                        tid="account_messages_subject"
                        namespace={TranslationScopes.UserAccount}
                    />
                    :<span>{conversation.topic}</span>
                    <span
                        className={classNames(conversation.is_starred && styles.active)}
                        onClick={onStarrClick}>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/messages-starred.svg`}
                            className="svg-icon"
                        />
                    </span>
                </div>
            </div>
        </div>
    );
};
export default Conversation;
